import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

export interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps {
    options: SelectOption[];
    value?: string | string[];
    onChange: (value: string | string[]) => void;
    placeholder?: string;
    label?: string;
    error?: string;
    disabled?: boolean;
    multiple?: boolean;
    className?: string;
}

const Select: React.FC<SelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select an option',
    label,
    error,
    disabled = false,
    multiple = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Handle clicking outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (optionValue: string) => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            const newValues = currentValues.includes(optionValue)
                ? currentValues.filter(v => v !== optionValue)
                : [...currentValues, optionValue];
            onChange(newValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
        }
    };

    const removeValue = (e: React.MouseEvent, valToRemove: string) => {
        e.stopPropagation();
        if (Array.isArray(value)) {
            onChange(value.filter(v => v !== valToRemove));
        }
    };

    const isSelected = (optionValue: string) => {
        if (multiple) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    const getDisplayValue = () => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return <span className="text-gray-400">{placeholder}</span>;
        }

        if (multiple && Array.isArray(value)) {
            return (
                <div className="flex flex-wrap gap-1">
                    {value.map(val => {
                        const option = options.find(o => o.value === val);
                        return (
                            <span
                                key={val}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-800 text-sm"
                            >
                                {option?.label || val}
                                <button
                                    type="button"
                                    onClick={(e) => removeValue(e, val)}
                                    className="hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        );
                    })}
                </div>
            );
        }

        const selectedOption = options.find(o => o.value === value);
        return <span className="text-gray-900">{selectedOption?.label || value}</span>;
    };

    return (
        <div className={`relative w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                </label>
            )}

            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`
          w-full px-3 py-2 border rounded-lg bg-white flex items-center justify-between cursor-pointer transition-all duration-200
          ${isOpen ? 'ring-2 ring-orange-500 border-transparent' : 'border-gray-300 hover:border-gray-400'}
          ${error ? 'border-red-300' : ''}
          ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-75' : ''}
        `}
            >
                <div className="flex-1 min-w-0 mr-2">
                    {getDisplayValue()}
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}

            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto py-1">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No options available
                        </div>
                    ) : (
                        options.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option.value)}
                                className={`
                  px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors
                  ${isSelected(option.value) ? 'bg-orange-50 text-orange-900' : 'text-gray-700 hover:bg-gray-50'}
                `}
                            >
                                <span>{option.label}</span>
                                {isSelected(option.value) && (
                                    <Check className="w-4 h-4 text-orange-600" />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default Select;
